import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { tokenManager } from '../lib/security';
import { markReturningUser } from '../lib/pwa';

import { API_URL } from '../lib/apiUrl';
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// ---- Helpers ----
const getCache = () => {
  try { const c = localStorage.getItem('mc_user'); return c ? JSON.parse(c) : null; } catch { return null; }
};
const setCache = (u) => {
  try { if (u) localStorage.setItem('mc_user', JSON.stringify(u)); else localStorage.removeItem('mc_user'); } catch {}
};

export const AuthProvider = ({ children }) => {
  // Initialize synchronously from cache — NO loading flash
  const [user, setUser] = useState(() => {
    const cached = getCache();
    const token = tokenManager.getToken();
    return token && cached ? cached : null;
  });

  const [token, setToken] = useState(() => {
    const t = tokenManager.getToken();
    if (t) axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    return t || null;
  });

  // loading=false from start if we have cache — prevents redirect flash
  const [loading, setLoading] = useState(() => {
    const t = tokenManager.getToken();
    const c = getCache();
    return !(t && c); // loading=false only if we have both token AND cache
  });

  const [networkError, setNetworkError] = useState(false);
  const refreshingRef = useRef(false);
  const interceptorRef = useRef(null);
  const mounted = useRef(true);
  const lastValidation = useRef(0);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const safeSet = useRef({
    user: (value) => { if (mounted.current) setUser(value); },
    token: (value) => { if (mounted.current) setToken(value); },
    loading: (value) => { if (mounted.current) setLoading(value); },
    network: (value) => { if (mounted.current) setNetworkError(value); },
  }).current;

  const doLogout = useCallback(async () => {
    tokenManager.clearAll(); setCache(null);
    delete axios.defaults.headers.common['Authorization'];
    if (mounted.current) { setToken(null); setUser(null); }
  }, []);

  // ---- Refresh Token ----
  const refreshAccessToken = useCallback(async () => {
    if (refreshingRef.current) {
      // Wait for ongoing refresh
      return new Promise(res => {
        const interval = setInterval(() => {
          if (!refreshingRef.current) { clearInterval(interval); res(tokenManager.getToken()); }
        }, 100);
      });
    }
    refreshingRef.current = true;
    try {
      const rt = tokenManager.getRefreshToken();
      if (!rt) return null;
      const r = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: rt });
      const { token: newToken, refresh_token: newRefresh } = r.data;
      tokenManager.setToken(newToken);
      tokenManager.setRefreshToken(newRefresh);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      safeSet.token(newToken);
      return newToken;
    } catch (err) {
      const status = err?.response?.status;
      console.warn('[Auth] Refresh failed:', status);
      // Rechazo de autenticación definitivo (token revocado, cuenta eliminada o
      // suspendida): cerramos sesión aunque haya caché, para no dejar una sesión
      // "zombi". Un fallo de red (sin response) sí conserva la caché.
      if (status === 401 || status === 403) {
        tokenManager.clearAll(); setCache(null);
        delete axios.defaults.headers.common['Authorization'];
        if (mounted.current) { setToken(null); setUser(null); }
      }
      return null;
    } finally { refreshingRef.current = false; }
  }, [safeSet]);

  // ---- Background validation (runs ONCE on mount, non-blocking) ----
  useEffect(() => {
    const validate = async () => {
      const now = Date.now();
      // Debounce: don't validate more than once every 10 seconds
      if (now - lastValidation.current < 10000) {
        safeSet.loading(false);
        return;
      }
      lastValidation.current = now;

      const currentToken = tokenManager.getToken();

      if (!currentToken) {
        const rt = tokenManager.getRefreshToken();
        if (rt) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            try {
              const r = await axios.get(`${API_URL}/api/auth/me`);
              safeSet.user(r.data); setCache(r.data);
              safeSet.token(newToken);
            } catch (e) {
              // Solo se cierra la sesión si el servidor RECHAZA la identidad.
              // Antes cualquier fallo (servidor reiniciándose, mala cobertura en
              // el campo, un 500 puntual) echaba al entrenador y le obligaba a
              // volver a entrar. Ahora se conserva la sesión y se avisa.
              const st = e?.response?.status;
              if (st === 401 || st === 403) doLogout();
              else { safeSet.token(newToken); safeSet.network(!e?.response); }
            }
          } else {
            doLogout();
          }
        } else {
          safeSet.user(null); setCache(null); safeSet.token(null);
        }
        safeSet.loading(false);
        return;
      }

      // Have token - use cache first for instant load, validate in background
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
        // Show cached user instantly
        const cached = getCache();
        if (cached) {
          safeSet.user(cached);
          safeSet.token(currentToken);
          safeSet.loading(false);
          // Valida en segundo plano, sin bloquear el pintado. Aqui NO se
          // vuelve a comprobar el antirebote: `lastValidation` ya se marco al
          // entrar en validate(), asi que la condicion era siempre cierta y
          // esta llamada nunca llegaba a hacerse. Resultado: la sesion en
          // cache no se refrescaba nunca, y un `access` viejo sobrevivia a
          // cambios reales de la cuenta (activar o cancelar la suscripcion).
          axios.get(`${API_URL}/api/auth/me`).then(r => {
            safeSet.user(r.data); setCache(r.data); safeSet.network(false);
          }).catch(() => {});
          return;
        }
        // No cache - must validate
        const r = await axios.get(`${API_URL}/api/auth/me`);
        safeSet.user(r.data); setCache(r.data);
        safeSet.token(currentToken);
        safeSet.network(false);
      } catch (error) {
        const status = error?.response?.status;
        const isNet = error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED' || !error?.response;

        if (isNet) {
          safeSet.network(true);
          // Keep cached user
          const cached = getCache();
          if (cached) safeSet.user(cached);
        } else if (status === 401) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            try {
              const r = await axios.get(`${API_URL}/api/auth/me`);
              safeSet.user(r.data); setCache(r.data);
            } catch (e2) {
              // Igual que arriba: un fallo del servidor no debe cerrar sesión.
              const st2 = e2?.response?.status;
              if (st2 === 401 || st2 === 403) doLogout();
              else {
                const cached2 = getCache();
                if (cached2) safeSet.user(cached2);
                safeSet.network(!e2?.response);
              }
            }
          } else {
            const cached = getCache();
            if (!cached) doLogout();
          }
        } else {
          // Server error (500 etc) - keep cached user, don't logout
          const cached = getCache();
          if (cached) safeSet.user(cached);
        }
      } finally {
        safeSet.loading(false);
      }
    };
    validate();
  }, [doLogout, refreshAccessToken, safeSet]);

  // ---- Periodic proactive refresh (every 20 min) ----
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!token || !mounted.current) return;
      const exp = localStorage.getItem('token_exp');
      if (exp && (parseInt(exp) - Date.now() / 1000) < 3600) {
        const newToken = await refreshAccessToken();
        if (newToken) safeSet.token(newToken);
      }
    }, 1200000); // 20 minutes
    return () => clearInterval(interval);
  }, [refreshAccessToken, safeSet, token]);

  // ---- Network detection ----
  useEffect(() => {
    const onOnline = async () => {
      safeSet.network(false);
      // Re-validate on reconnect
      if (token) {
        try {
          const r = await axios.get(`${API_URL}/api/auth/me`);
          safeSet.user(r.data); setCache(r.data);
        } catch {
          const newToken = await refreshAccessToken();
          if (!newToken && !getCache()) doLogout();
        }
      }
    };
    const onOffline = () => safeSet.network(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, [doLogout, refreshAccessToken, safeSet, token]);

  // ---- Axios 401 interceptor ----
  useEffect(() => {
    if (interceptorRef.current !== null) axios.interceptors.response.eject(interceptorRef.current);
    interceptorRef.current = axios.interceptors.response.use(
      res => { safeSet.network(false); return res; },
      async error => {
        // Navigating between workspaces deliberately cancels stale requests.
        // A cancellation is not a connection failure or a reason to log out.
        if (error.code === 'ERR_CANCELED' || error.__CANCEL__) return Promise.reject(error);
        const req = error.config;
        if (req?.url?.includes('/auth/refresh') || req?.url?.includes('/auth/login')) return Promise.reject(error);
        if (error.response?.status === 401 && !req._retry) {
          req._retry = true;
          const newToken = await refreshAccessToken();
          if (newToken) {
            req.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(req);
          }
          const cached = getCache();
          if (!cached && mounted.current) doLogout();
        }
        // 402: el muro de pago ha cortado la petición. Es la fuente de verdad,
        // así que mandamos al usuario al alta aunque su caché diga otra cosa.
        if (error.response?.status === 402
            && error.response?.data?.detail?.code === 'subscription_required'
            && !window.location.pathname.startsWith('/onboarding')) {
          const cached = getCache();
          if (cached) {
            const updated = { ...cached, access: { ...(cached.access || {}), blocked: true,
              reason: error.response.data.detail.reason } };
            setCache(updated); safeSet.user(updated);
          }
          window.location.replace('/onboarding');
        }
        if (!error.response && mounted.current) safeSet.network(true);
        return Promise.reject(error);
      }
    );
    return () => { if (interceptorRef.current !== null) axios.interceptors.response.eject(interceptorRef.current); };
  }, [doLogout, refreshAccessToken, safeSet]);

  const login = async (email, password) => {
    const r = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: t, refresh_token: rt, user: u } = r.data;
    try { localStorage.removeItem('mc_player_token'); } catch {}
    tokenManager.setToken(t); tokenManager.setRefreshToken(rt);
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    setToken(t); setUser(u); setCache(u); setNetworkError(false);
    markReturningUser(u?.role === 'parent' ? 'parent' : 'coach');
    lastValidation.current = Date.now(); // Prevent immediate re-validation
    return u;
  };

  const register = async (email, password, name, selected_plan = 'free') => {
    const r = await axios.post(`${API_URL}/api/auth/register`, { email, password, name, selected_plan });
    return r.data;
  };

  // Inicia sesión a partir de tokens ya emitidos (p. ej. tras verificar el email).
  const applySession = ({ token: t, refresh_token: rt, user: u }) => {
    try { localStorage.removeItem('mc_player_token'); } catch {}
    tokenManager.setToken(t); tokenManager.setRefreshToken(rt);
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    setToken(t); setUser(u); setCache(u); setNetworkError(false);
    markReturningUser(u?.role === 'parent' ? 'parent' : 'coach');
    lastValidation.current = Date.now();
    return u;
  };

  // Relee el usuario del servidor y refresca la cache. Hace falta cuando algo
  // cambia el estado de la cuenta por fuera de la sesion (por ejemplo, activar
  // la suscripcion al volver de Stripe): sin esto los guardianes de ruta siguen
  // leyendo el `access` viejo de la cache y rebotan al usuario al muro de pago.
  const refreshUser = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/auth/me`);
      safeSet.user(r.data); setCache(r.data);
      lastValidation.current = Date.now();
      return r.data;
    } catch {
      return null;
    }
  }, [safeSet]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, networkError,
      login, register, applySession, logout: doLogout, refreshUser,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
};
